// Receptionist · Call Logs — full view
// Presentation/state-only: mock data + local React state, no API or persistence.

import React, { useMemo, useState } from 'react';
import {
  CALL_LOGS_TABS,
  CL_TOKENS,
} from '../../receptionist-call-logs/config';
import { INBOUND_CALLS, OUTBOUND_PATIENT_CALLS } from '../../receptionist-call-logs/mockData';
import type {
  CallLogsTabId,
  CallLogsTimeRange,
  CallLogsViewProps,
  InboundCall,
  OutboundPatientCall,
} from '../../receptionist-call-logs';
import { Icon } from '../Icon/Icon';
import { ViewportDrawer } from '../Overlay/ViewportDrawer';
import { CallDetailDrawerSwitch } from './CallLogsView.helpers';
import { CallLogsFilterBar } from './CallLogsFilterBar';
import { CallLogsKpis } from './CallLogsKpis';
import { CallLogsTable } from './CallLogsTable';
import './callLogs.css';

function matchesSearch(name: string, phone: string, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return name.toLowerCase().includes(q) || phone.toLowerCase().includes(q);
}

function PageHeader({
  activeTab,
  onTabChange,
}: {
  activeTab: CallLogsTabId;
  onTabChange: (id: CallLogsTabId) => void;
}) {
  const counts: Record<CallLogsTabId, number> = {
    inbound: INBOUND_CALLS.length,
    outboundPatient: OUTBOUND_PATIENT_CALLS.length,
  };
  return (
    <section className="cl-fade-up cl-d-0 mb-[18px]">
      <div className="mb-2.5 flex items-center gap-2">
        <Icon kind="sparkle" size={13} color={CL_TOKENS.green} stroke={2} />
        <span className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: CL_TOKENS.green }}>
          Call logs · activity
        </span>
      </div>
      <h1 className="m-0 text-[46px] font-semibold leading-none tracking-[-0.035em] text-[var(--ink-0)]">
        Call <span className="cl-count-gradient">logs</span>
      </h1>
      <p className="mb-5 mt-3 max-w-[700px] text-[14px] font-normal leading-[1.55] tracking-[-0.005em]" style={{ color: CL_TOKENS.ink3 }}>
        Every conversation Elva handled, with full context and recordings.
      </p>

      <div className="flex items-center gap-1 border-b border-[var(--cream-2)]">
        {CALL_LOGS_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} type="button" className="cl-tab" data-active={isActive} onClick={() => onTabChange(tab.id)}>
              <span className="inline-flex items-center gap-[7px]">
                {tab.label}
                <span
                  className="cl-mono rounded-[5px] px-1.5 py-px text-[11px] font-medium"
                  style={{
                    color: isActive ? CL_TOKENS.ink2 : CL_TOKENS.ink4,
                    background: isActive ? CL_TOKENS.surfaceMute : 'transparent',
                  }}
                >
                  {counts[tab.id]}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function CallLogsView({ initialTab = 'inbound' }: CallLogsViewProps = {}) {
  const [activeTab, setActiveTab] = useState<CallLogsTabId>(initialTab ?? 'inbound');
  const [timeRange, setTimeRange] = useState<CallLogsTimeRange>('today');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const changeTab = (id: CallLogsTabId) => {
    setActiveTab(id);
    setSelectedId(null);
  };

  const inboundCalls = useMemo<InboundCall[]>(
    () => INBOUND_CALLS.filter((c) => matchesSearch(c.name, c.phone, search)),
    [search],
  );
  const outboundCalls = useMemo<OutboundPatientCall[]>(
    () => OUTBOUND_PATIENT_CALLS.filter((c) => matchesSearch(c.name, c.phone, search)),
    [search],
  );

  const selectedInbound =
    activeTab === 'inbound' && selectedId ? inboundCalls.find((c) => c.id === selectedId) ?? null : null;
  const selectedOutbound =
    activeTab === 'outboundPatient' && selectedId
      ? outboundCalls.find((c) => c.id === selectedId) ?? null
      : null;

  const drawerOpen = !!(selectedInbound || selectedOutbound);

  return (
    <>
    <div className="cl-root min-h-full w-full overflow-auto px-7 pb-9 pt-[22px]">
      <div className="mx-auto flex w-full max-w-[1320px] flex-col">
        <PageHeader activeTab={activeTab} onTabChange={changeTab} />
        <CallLogsKpis activeTab={activeTab} />
        <CallLogsFilterBar
          activeTab={activeTab}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          search={search}
          onSearchChange={setSearch}
        />

        <div className="relative">
          <CallLogsTable
            activeTab={activeTab}
            inboundCalls={inboundCalls}
            outboundCalls={outboundCalls}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </div>

        <footer
          className="mt-6 flex items-center gap-2.5 border-t border-[var(--cream-2)] px-1 py-4 text-[12px]"
          style={{ color: CL_TOKENS.ink3 }}
        >
          <span className="elva-live h-1.5 w-1.5" />
          <span className="font-medium">Live</span>
          <span className="cl-mono">· Logs update as calls complete</span>
        </footer>
      </div>
    </div>

    <ViewportDrawer
      open={drawerOpen}
      onClose={() => setSelectedId(null)}
      width="520px"
      panelClassName="overflow-hidden rounded-l-[16px] border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-3)]"
    >
      <CallDetailDrawerSwitch
        inbound={selectedInbound}
        outbound={selectedOutbound}
        onClose={() => setSelectedId(null)}
      />
    </ViewportDrawer>
    </>
  );
}
